import { HttpClient, HttpParams } from '@angular/common/http'
import { EventEmitter, Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import { ResponseModel, ResultCode } from '../domain/authentication.model'
import { BaseGetModel } from '../domain/base-get-model'
import { CompilationTypes, SortBy } from '../enums/enums'
import { IBeatDictionaries, IBeatDictionaryItem } from '../Interface/beat-dictionary'
import { IMood } from '../Interface/mood'
import { IStyle } from '../Interface/style'
import { ITrack } from '../Interface/track'
import { PlayerLogicTrackType } from '../player/common/Types'
import { MultiSelectMode } from '../types/filter.type'
import { CoreEnvironmentService } from './core-environment.service'
import { InternationalizationService } from './internationalization.service'
import { NotificationService } from './notification.service'
import { ParamsService } from './params.service'

/**
 * Параметры выдачи каталога одним объектом.
 * Раньше getBeats принимал девять позиционных аргументов — каждый новый фильтр
 * ломал все вызовы и легко было перепутать порядок.
 */
export type GetBeatsParams = {
  beatmakerId?: number
  styleId?: number
  moodId?: number
  sortBy?: SortBy
  pageNo?: number
  pageSize?: number
  compilationType?: CompilationTypes
  beatName?: string
  /** одиночный тег: старый параметр, поддерживается ради существующих ссылок */
  tagId?: number
  tagIds?: number[]
  tagsMode?: MultiSelectMode
  instrumentIds?: number[]
  instrumentsMode?: MultiSelectMode
  keyIds?: number[]
  trackTypeIds?: number[]
  bpmFrom?: number
  bpmTo?: number
  priceFrom?: number
  priceTo?: number
}

@Injectable()
export class TracksService extends ParamsService {
  changeFavoriteStatusEvent = new EventEmitter<number>()
  compilationTypes: typeof CompilationTypes = CompilationTypes
  private controller = '/web/v1/'
  private urls: {
    _tracksUrl: string
    _specialPromoTracksUrl: string
    _freeBeatsUrl: string
    _moodsUri: string
    _stylesUri: string
    _dictionariesUri: string
    _toggleFavorite: string
    _removeFromFavorite: string
    _countTrackPlays: string
    _incrementTrackDownloads: string
  }
  private baseURL: string

  Moods: BehaviorSubject<IMood[]> = new BehaviorSubject<IMood[]>([])
  Styles: BehaviorSubject<IStyle[]> = new BehaviorSubject<IStyle[]>([])
  Keys: BehaviorSubject<IBeatDictionaryItem[]> = new BehaviorSubject<IBeatDictionaryItem[]>([])
  Instruments: BehaviorSubject<IBeatDictionaryItem[]> = new BehaviorSubject<IBeatDictionaryItem[]>([])
  TrackTypes: BehaviorSubject<IBeatDictionaryItem[]> = new BehaviorSubject<IBeatDictionaryItem[]>([])

  constructor(
    protected http: HttpClient,
    protected coreEnvironmentService: CoreEnvironmentService,
    private internationalizationService: InternationalizationService,
    private notificationService: NotificationService
  ) {
    super()

    this.baseURL = coreEnvironmentService.getBaseURL()

    this.urls = {
      _tracksUrl: this.baseURL + this.controller + 'beats',
      _specialPromoTracksUrl: this.baseURL + this.controller + 'beats/special-catalog',
      _freeBeatsUrl: this.baseURL + this.controller + 'beats/free',
      _moodsUri: this.baseURL + this.controller + 'moods',
      _stylesUri: this.baseURL + this.controller + 'styles',
      _dictionariesUri: this.baseURL + this.controller + 'beats/dictionaries',
      _toggleFavorite: this.baseURL + this.controller + 'beats/toggleFavorite',
      _removeFromFavorite: this.baseURL + this.controller + 'beats/removeFromFavorite',
      _countTrackPlays: this.baseURL + '/mp3/mp3/play',
      _incrementTrackDownloads: this.baseURL + '/beats/Beats/DownloadBeat'
    }

    this.internationalizationService.currentLanguage.subscribe(() => {
      this.refreshMoods()
      this.refreshStyles()
      this.refreshBeatDictionaries()
    })
  }

  getBeats(params: GetBeatsParams = {}): Observable<BaseGetModel<ITrack>> {
    return this.http.get<BaseGetModel<ITrack>>(this.urls._tracksUrl, { params: this.setParams(params) })
  }

  getBeatById(beatId: number): Observable<ITrack> {
    const resultObservable = this.http.get<ITrack>(`${this.urls._tracksUrl}/${beatId}`)
    return resultObservable.pipe<ITrack>(map((resp: ITrack) => resp))
  }

  private refreshMoods(): void {
    this.http.get<BaseGetModel<IMood>>(`${this.urls._moodsUri}`).subscribe(moods => {
      this.Moods.next(moods.items)
    })
  }

  private refreshStyles(): void {
    this.http.get<BaseGetModel<IStyle>>(`${this.urls._stylesUri}`).subscribe(res => {
      this.Styles.next(res.items)
    })
  }

  /** Тональности, инструменты и типы трека приходят одним запросом */
  private refreshBeatDictionaries(): void {
    this.http.get<IBeatDictionaries>(`${this.urls._dictionariesUri}`).subscribe(res => {
      this.Keys.next(res.keys ?? [])
      this.Instruments.next(res.instruments ?? [])
      this.TrackTypes.next(res.trackTypes ?? [])
    })
  }

  getStyles(): Observable<BaseGetModel<IStyle>> {
    return this.http.get<BaseGetModel<IStyle>>(`${this.urls._stylesUri}`)
  }

  changeBeatFavoriteStatus(beatId: number) {
    this.http.post<ResponseModel>(this.urls._toggleFavorite, { beatId }, { withCredentials: true }).subscribe(
      res => {
        if (res.resultCode === ResultCode.Success) {
          this.changeFavoriteStatusEvent.emit(beatId)
        } else {
          this.notificationService.handleError(res.messages[0])
        }
      },
      err => this.notificationService.handleError(err.message)
    )
  }

  getCatalogBeats(styleId?: number, moodId?: number, sortBy?: number, pageNo?: number, pageSize?: number) {
    return this.getBeats({
      styleId,
      moodId,
      sortBy,
      pageNo,
      pageSize,
      compilationType: this.compilationTypes.CatalogBeats
    })
  }

  getCatalogBeatsForCurrentBeatmaker(beatmakerId: number, sortBy?: number, pageNo?: number, pageSize?: number) {
    return this.getBeats({
      beatmakerId,
      sortBy,
      pageNo,
      pageSize,
      compilationType: this.compilationTypes.CatalogBeats
    })
  }

  getSpotlightBeats(pageSize: number) {
    return this.getCompilationBeats(this.compilationTypes.Spotlight, pageSize)
  }

  getChartToppersBeats(pageSize?: number) {
    return this.getCompilationBeats(this.compilationTypes.ChartToppers, pageSize)
  }

  // unused method because producerService.getDaySeller() return information about beats
  getTopDaySellerBeats(pageSize?: number) {
    return this.getCompilationBeats(this.compilationTypes.TopDaySeller, pageSize)
  }

  getHotNewBeats(pageSize?: number) {
    return this.getCompilationBeats(this.compilationTypes.HotNewBeats, pageSize)
  }

  getEditorChoiceBeats(pageSize?: number) {
    return this.getCompilationBeats(this.compilationTypes.EditorChoice, pageSize)
  }

  getFavoriteBeats(pageSize?: number) {
    return this.getCompilationBeats(this.compilationTypes.Favorites, pageSize)
  }

  private getCompilationBeats(compilationType: CompilationTypes, pageSize?: number) {
    return this.getBeats({ pageNo: 1, pageSize, compilationType })
  }

  getSpecialPromoBeats(): Observable<BaseGetModel<ITrack>> {
    return this.http.get<BaseGetModel<ITrack>>(this.urls._specialPromoTracksUrl).pipe(
      tap(resp => {
        if (resp && Array.isArray(resp.items)) {
          resp.items.forEach(track => {
            track.isPromo = true
          })
        }
      })
    )
  }

  incrementTrackPlays(track: PlayerLogicTrackType<ITrack>) {
    const trackTypeMap: Record<string, number> = {
      'free-beat': 0,
      beat: 1,
      song: 2
    }

    const trackType = trackTypeMap[track.type] ?? 1

    return this.http.get(`${this.urls._countTrackPlays}?trackId=${track.id}&type=${trackType}`, {
      responseType: 'text'
    })
  }

  getFreeBeats(
    styleId?: number,
    moodId?: number,
    sortBy?: SortBy,
    pageNo?: number,
    pageSize?: number
  ): Observable<BaseGetModel<ITrack>> {
    // beatmakerId is not applicable for free beats
    const params = this.setParams({ styleId, moodId, sortBy, pageNo, pageSize })

    return this.http.get<BaseGetModel<ITrack>>(this.urls._freeBeatsUrl, { params })
  }

  incrementTrackDownloads(trackId: number) {
    return this.http.get(`${this.urls._incrementTrackDownloads}?beatId=${trackId}`)
  }

  private setParams(model: GetBeatsParams): HttpParams {
    let params = new HttpParams()
    params = this.setParam(params, 'beatmakerId', model.beatmakerId)
    params = this.setParam(params, 'styleId', model.styleId)
    params = this.setParam(params, 'moodId', model.moodId)
    params = this.setParam(params, 'sortBy', model.sortBy)
    params = this.setParam(params, 'pageNo', model.pageNo)
    params = this.setParam(params, 'pageSize', model.pageSize)
    params = this.setParam(params, 'compilationType', model.compilationType)
    params = this.setParam(params, 'beatName', model.beatName)
    params = this.setParam(params, 'tagId', model.tagId)

    // бэкенд принимает списки и через запятую, и повторяющимися параметрами
    params = this.setIdsParam(params, 'tagIds', model.tagIds)
    params = this.setIdsParam(params, 'instrumentIds', model.instrumentIds)
    params = this.setIdsParam(params, 'keyIds', model.keyIds)
    params = this.setIdsParam(params, 'trackTypeIds', model.trackTypeIds)

    // режим имеет смысл только когда выбрано больше одного значения
    if (model.tagIds?.length > 1) {
      params = this.setParam(params, 'tagsMode', this.toModeParam(model.tagsMode))
    }
    if (model.instrumentIds?.length > 1) {
      params = this.setParam(params, 'instrumentsMode', this.toModeParam(model.instrumentsMode))
    }

    params = this.setParam(params, 'bpmFrom', model.bpmFrom)
    params = this.setParam(params, 'bpmTo', model.bpmTo)
    params = this.setParam(params, 'priceFrom', model.priceFrom)
    params = this.setParam(params, 'priceTo', model.priceTo)

    return params
  }

  private setIdsParam(params: HttpParams, name: string, ids?: number[]): HttpParams {
    return ids?.length ? this.setParam(params, name, ids.join(',')) : params
  }

  /** на бэкенде это enum MultiSelectMode { And, Or } */
  private toModeParam(mode?: MultiSelectMode): string {
    return mode === 'or' ? 'Or' : 'And'
  }
}
